import { descriptorsFromLogList, isLogListJson, type LogListJson } from './schema';
import type { ProgramId } from './sources';
import type { LogDescriptor } from '../log/client';

export const LOG_LIST_SOURCES = {
  log_list: 'https://www.gstatic.com/ct/log_list/v3/log_list.json',
  all_logs_list: 'https://www.gstatic.com/ct/log_list/v3/all_logs_list.json',
} as const;
export type LogListSource = keyof typeof LOG_LIST_SOURCES | (string & {});

export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface LoadedLogList {
  url: string;
  version?: string;
  timestamp?: string;
  fetchedAt: number;
  fromCache: boolean;
  descriptors: LogDescriptor[];
}

export function resolveLogListUrl(source: LogListSource): string {
  return (LOG_LIST_SOURCES as Record<string, string>)[source] ?? source;
}

interface Cached {
  fetchedAt: number;
  body: string;
}

/**
 * Loads a log list (gstatic serves it with CORS), caching the JSON text with a TTL. When the
 * network fails but a stale copy exists, the stale copy is returned with `fromCache: true`.
 */
export async function loadLogList(
  source: LogListSource,
  opts: { cache?: KeyValueStore; ttlMs?: number; force?: boolean; fetchFn?: typeof fetch; signal?: AbortSignal; program?: ProgramId } = {},
): Promise<LoadedLogList> {
  const program = opts.program ?? 'chrome';
  const url = resolveLogListUrl(source);
  const key = `ct:loglist:v1:${url}`;
  const ttl = opts.ttlMs ?? 60 * 60 * 1000;
  let cached: Cached | undefined;
  try {
    const raw = opts.cache?.getItem(key);
    if (raw) cached = JSON.parse(raw) as Cached;
  } catch {
    cached = undefined;
  }
  if (cached && !opts.force && Date.now() - cached.fetchedAt < ttl) {
    return parse(cached.body, url, cached.fetchedAt, true, program);
  }
  try {
    const res = await (opts.fetchFn ?? fetch)(url, { signal: opts.signal ?? null });
    if (!res.ok) throw new Error(`log list: HTTP ${res.status}`);
    const body = await res.text();
    const loaded = parse(body, url, Date.now(), false, program);
    try {
      opts.cache?.setItem(key, JSON.stringify({ fetchedAt: loaded.fetchedAt, body } satisfies Cached));
    } catch {
      /* storage full or unavailable */
    }
    return loaded;
  } catch (e) {
    if (cached) return parse(cached.body, url, cached.fetchedAt, true, program);
    throw e;
  }
}

function parse(body: string, url: string, fetchedAt: number, fromCache: boolean, program: ProgramId): LoadedLogList {
  const json: unknown = JSON.parse(body);
  if (!isLogListJson(json)) throw new Error('log list: unexpected JSON shape');
  const list = json as LogListJson;
  const out: LoadedLogList = { url, fetchedAt, fromCache, descriptors: descriptorsFromLogList(list, program) };
  if (list.version) out.version = list.version;
  if (list.log_list_timestamp) out.timestamp = list.log_list_timestamp;
  return out;
}
